// Story catalogue. One entry per persona id; stories appear in the discover
// row when the relationship is matched. Ported verbatim from the beta.
//
// STORIES_ENABLED is the single kill-switch for the whole story feature:
// - When false: `storiesFor` returns []; `useDailyStories` skips the fetch;
//   MatchesScreen hides the ring row; StoryViewer never renders (guarded by
//   storyList.length > 0). No /api/stories request goes out.
// - Flip to true to bring the feature back with no other code changes.
// Also gate the server: server/src/index.ts has a matching STORIES_ENABLED.

import type { Story } from '@/lib/types';

export const STORIES_ENABLED = false;

export const STORIES: Readonly<Record<string, Story[]>> = {
  xiaomei: [
    {
      scene: 'cafe-morning',
      hz: '今天早上的拿铁，特别好喝 ☕',
      py: 'Jīntiān zǎoshang de nátiě, tèbié hǎohē',
      en: "This morning's latte was extra good",
      words: [
        { hz: '今天', py: 'jīntiān', en: 'today' },
        { hz: '早上', py: 'zǎoshang', en: 'morning' },
        { hz: '拿铁', py: 'nátiě', en: 'latte' },
        { hz: '好喝', py: 'hǎohē', en: 'tasty (drinks)' },
      ],
      timeAgo: '2h',
    },
    {
      scene: 'cafe-cat',
      hz: '店里来了一只小猫，太可爱了！',
      py: 'Diàn lǐ láile yì zhī xiǎo māo, tài kě\'ài le!',
      en: 'A little cat came to the shop — so cute!',
      words: [
        { hz: '店', py: 'diàn', en: 'shop' },
        { hz: '小猫', py: 'xiǎo māo', en: 'kitten' },
        { hz: '可爱', py: 'kě\'ài', en: 'cute' },
      ],
      timeAgo: '5h',
    },
  ],
  dalong: [
    {
      scene: 'mountain',
      hz: '山中无日历，云开见月明。',
      py: 'Shān zhōng wú rìlì, yún kāi jiàn yuè míng.',
      en: 'In the mountains there is no calendar; when the clouds part, the moon appears.',
      words: [
        { hz: '山', py: 'shān', en: 'mountain' },
        { hz: '云', py: 'yún', en: 'cloud' },
        { hz: '月', py: 'yuè', en: 'moon' },
      ],
      timeAgo: '1d',
    },
    {
      scene: 'tea',
      hz: '今晚的茶，是去年的春茶。',
      py: 'Jīnwǎn de chá, shì qùnián de chūnchá.',
      en: "Tonight's tea is from last spring.",
      words: [
        { hz: '今晚', py: 'jīnwǎn', en: 'tonight' },
        { hz: '茶', py: 'chá', en: 'tea' },
        { hz: '春', py: 'chūn', en: 'spring' },
      ],
      timeAgo: '1d',
    },
  ],
  xiaofang: [
    {
      scene: 'books-quiet',
      hz: '一个人复习，有点累…',
      py: 'Yí gè rén fùxí, yǒudiǎn lèi…',
      en: 'Studying alone, a little tired…',
      words: [
        { hz: '一个人', py: 'yí gè rén', en: 'alone' },
        { hz: '累', py: 'lèi', en: 'tired' },
      ],
      timeAgo: '3d',
    },
  ],
  laowang: [
    {
      scene: 'empty-road',
      hz: '今天路上人少，安静。',
      py: 'Jīntiān lù shàng rén shǎo, ānjìng.',
      en: "Few people on the road today. Quiet.",
      words: [
        { hz: '路', py: 'lù', en: 'road' },
        { hz: '安静', py: 'ānjìng', en: 'quiet' },
      ],
      timeAgo: '1w',
    },
  ],
  // Generic-scene stories for the remaining 16 personas — one per character.
  linlaoshi: [
    {
      scene: 'kindergarten',
      hz: '小朋友画了一张画送我，太可爱了。',
      py: 'Xiǎopéngyǒu huàle yì zhāng huà sòng wǒ, tài kě\'ài le.',
      en: 'A little student drew me a picture. So cute.',
      words: [
        { hz: '小朋友', py: 'xiǎopéngyǒu', en: 'kid' },
        { hz: '画', py: 'huà', en: 'picture / draw' },
        { hz: '送', py: 'sòng', en: 'to give' },
      ],
      timeAgo: '4h',
    },
  ],
  aming: [
    {
      scene: 'bubbletea-shop',
      hz: '今天卖了一百杯奶茶，累死了…',
      py: 'Jīntiān màile yìbǎi bēi nǎichá, lèi sǐ le…',
      en: 'Sold a hundred milk teas today. Dead tired…',
      words: [
        { hz: '卖', py: 'mài', en: 'to sell' },
        { hz: '杯', py: 'bēi', en: 'cup (measure word)' },
        { hz: '累', py: 'lèi', en: 'tired' },
      ],
      timeAgo: '6h',
    },
  ],
  xiaoyu: [
    {
      scene: 'terracotta',
      hz: '又给一群学生讲秦始皇。声音都哑了。',
      py: 'Yòu gěi yì qún xuéshēng jiǎng Qín Shǐhuáng. Shēngyīn dōu yǎ le.',
      en: "Told another tour group about Qin Shi Huang. Voice is gone.",
      words: [
        { hz: '学生', py: 'xuéshēng', en: 'student' },
        { hz: '讲', py: 'jiǎng', en: 'to tell / explain' },
        { hz: '声音', py: 'shēngyīn', en: 'voice' },
      ],
      timeAgo: '1d',
    },
  ],
  ajie: [
    {
      scene: 'shanghai-noodles',
      hz: '兄弟们！这家小笼包，绝了！',
      py: 'Xiōngdìmen! Zhè jiā xiǎolóngbāo, jué le!',
      en: 'Brothers! This place\'s xiaolongbao — incredible!',
      words: [
        { hz: '小笼包', py: 'xiǎolóngbāo', en: 'soup dumplings' },
        { hz: '绝', py: 'jué', en: 'amazing (slang)' },
      ],
      timeAgo: '3h',
    },
  ],
  meiling: [
    {
      scene: 'hk-fashion',
      hz: '中环今天人太多了。回家。',
      py: 'Zhōnghuán jīntiān rén tài duō le. Huí jiā.',
      en: 'Central is too crowded today. Going home.',
      words: [
        { hz: '中环', py: 'Zhōnghuán', en: 'Central (HK)' },
        { hz: '回家', py: 'huí jiā', en: 'go home' },
      ],
      timeAgo: '2h',
    },
  ],
  aqiang: [
    {
      scene: 'shenzhen-tech',
      hz: '又加班到十点。这个 bug 真的离谱。',
      py: 'Yòu jiābān dào shí diǎn. Zhè ge bug zhēn de lípǔ.',
      en: 'Working overtime till 10 again. This bug is unreal.',
      words: [
        { hz: '加班', py: 'jiābān', en: 'overtime' },
        { hz: '十点', py: 'shí diǎn', en: '10 o\'clock' },
        { hz: '离谱', py: 'lípǔ', en: 'absurd / unreal' },
      ],
      timeAgo: '5h',
    },
  ],
  laoli: [
    {
      scene: 'yunnan-market',
      hz: '今年的野生菌，特别好。来云南找我。',
      py: 'Jīnnián de yěshēngjùn, tèbié hǎo. Lái Yúnnán zhǎo wǒ.',
      en: "This year's wild mushrooms are especially good. Come to Yunnan, find me.",
      words: [
        { hz: '今年', py: 'jīnnián', en: 'this year' },
        { hz: '野生菌', py: 'yěshēngjùn', en: 'wild mushrooms' },
        { hz: '云南', py: 'Yúnnán', en: 'Yunnan' },
      ],
      timeAgo: '8h',
    },
  ],
  xueer: [
    {
      scene: 'harbin-ice',
      hz: '今天零下二十八度。手都冻僵了。',
      py: 'Jīntiān líng xià èrshíbā dù. Shǒu dōu dòng jiāng le.',
      en: 'Minus 28 today. My hands are frozen stiff.',
      words: [
        { hz: '零下', py: 'líng xià', en: 'below zero' },
        { hz: '冻', py: 'dòng', en: 'frozen' },
        { hz: '僵', py: 'jiāng', en: 'stiff' },
      ],
      timeAgo: '12h',
    },
  ],
  dawei: [
    {
      scene: 'qingdao-sea',
      hz: '今天没出海，风太大了。喝啤酒吧。',
      py: 'Jīntiān méi chū hǎi, fēng tài dà le. Hē píjiǔ ba.',
      en: "Didn't go out today, too windy. Time for beer.",
      words: [
        { hz: '出海', py: 'chū hǎi', en: 'go out to sea' },
        { hz: '风', py: 'fēng', en: 'wind' },
        { hz: '啤酒', py: 'píjiǔ', en: 'beer' },
      ],
      timeAgo: '3h',
    },
  ],
  lili: [
    {
      scene: 'wuhan-hospital',
      hz: '上了一个夜班。现在就想睡觉。',
      py: 'Shàngle yí gè yèbān. Xiànzài jiù xiǎng shuìjiào.',
      en: 'Worked a night shift. All I want is sleep.',
      words: [
        { hz: '夜班', py: 'yèbān', en: 'night shift' },
        { hz: '睡觉', py: 'shuìjiào', en: 'to sleep' },
      ],
      timeAgo: '5h',
    },
  ],
  atao: [
    {
      scene: 'macau-casino',
      hz: '昨晚一个人输了五十万。还笑着走了。',
      py: 'Zuó wǎn yí gè rén shūle wǔshí wàn. Hái xiàozhe zǒu le.',
      en: "Last night a guy lost 500k. Walked out smiling.",
      words: [
        { hz: '昨晚', py: 'zuó wǎn', en: 'last night' },
        { hz: '输', py: 'shū', en: 'to lose' },
        { hz: '笑', py: 'xiào', en: 'to laugh / smile' },
      ],
      timeAgo: '10h',
    },
  ],
  wangjiaoshou: [
    {
      scene: 'beijing-prof',
      hz: '又重读了一遍《呐喊》。每次都不一样。',
      py: 'Yòu chóng dúle yí biàn "Nàhǎn". Měi cì dōu bù yíyàng.',
      en: 'Re-read "Call to Arms" again. Different every time.',
      words: [
        { hz: '重读', py: 'chóng dú', en: 'reread' },
        { hz: '呐喊', py: 'nàhǎn', en: 'Call to Arms (Lu Xun)' },
      ],
      timeAgo: '1d',
    },
  ],
  ziqing: [
    {
      scene: 'suzhou-silk',
      hz: '这块染了三天。颜色刚刚好。',
      py: 'Zhè kuài rǎnle sān tiān. Yánsè gānggāng hǎo.',
      en: 'This piece took three days to dye. Color is just right.',
      words: [
        { hz: '染', py: 'rǎn', en: 'to dye' },
        { hz: '颜色', py: 'yánsè', en: 'color' },
        { hz: '刚刚好', py: 'gānggāng hǎo', en: 'just right' },
      ],
      timeAgo: '6h',
    },
  ],
  laochen: [
    {
      scene: 'chongqing-hotpot',
      hz: '今晚的麻辣锅！排队两小时也值！',
      py: 'Jīnwǎn de málà guō! Páiduì liǎng xiǎoshí yě zhí!',
      en: "Tonight's spicy hotpot! Worth a 2-hour wait!",
      words: [
        { hz: '麻辣', py: 'málà', en: 'numbing-spicy' },
        { hz: '排队', py: 'páiduì', en: 'queue / line up' },
        { hz: '值', py: 'zhí', en: 'worth it' },
      ],
      timeAgo: '4h',
    },
  ],
  jingyi: [
    {
      scene: 'beijing-editor',
      hz: '一个新人作家，第一本书。写得真好。',
      py: 'Yí gè xīnrén zuòjiā, dì yī běn shū. Xiě de zhēn hǎo.',
      en: "A debut author's first book. Really well-written.",
      words: [
        { hz: '作家', py: 'zuòjiā', en: 'author' },
        { hz: '第一', py: 'dì yī', en: 'first' },
        { hz: '本', py: 'běn', en: 'book (measure word)' },
      ],
      timeAgo: '8h',
    },
  ],
  xiaoma: [
    {
      scene: 'mongolia-grassland',
      hz: '今天天空很大。',
      py: 'Jīntiān tiānkōng hěn dà.',
      en: 'The sky is wide today.',
      words: [
        { hz: '天空', py: 'tiānkōng', en: 'sky' },
        { hz: '大', py: 'dà', en: 'big / wide' },
      ],
      timeAgo: '2d',
    },
  ],
};

export function storiesFor(
  personaId: string,
  daily?: Record<string, Story[]> | null,
): Story[] {
  if (!STORIES_ENABLED) return [];
  if (daily && daily[personaId]) return daily[personaId]!;
  return STORIES[personaId] ?? [];
}
